"use client";

import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

interface MetadataItem {
  id: string;
  icon: string;
  label: string;
  status: boolean;
  icon_alt: string;
  priority: number;
  text_color: string;
}

interface FeatureItem {
  id: string;
  icon: string;
  label: string;
  status: boolean;
  icon_alt: string;
  priority: number;
  text_color: string;
  description: string;
  value_text_color: string;
}

interface CategoryRow {
  id: string;
  label: string;
  value: string;
  status: boolean;
  is_total: boolean;
  priority: number;
  text_color: string;
  value_font_size: string;
  value_text_color: string;
}

interface Category {
  id: string;
  title: string;
  bg_color?: string;
  metadata: CategoryRow[];
  text_color?: string;
  order_index: number;
  border_color?: string;
  active_bg_color?: string;
  active_text_color?: string;
}

interface ProgramFee2Data {
  title_1: string;
  title_1_color: string;
  title_2: string;
  title_2_gradient_from: string;
  title_2_gradient_to: string;
  title_2_gradient_angle: number | null;
  table_gradient_from: string;
  table_gradient_to: string;
  table_gradient_angle: number | null;
  metadata_title: string;
  metadata_title_color: string;
  metadata: MetadataItem[];
  metadata_1: FeatureItem[];
  categories: Category[];
  cta_text: string;
  cta_text_color: string;
  cta_gradient_from: string;
  cta_gradient_to: string;
  cta_gradient_angle: number | null;
  background_color: string;
  enable_animation?: boolean | number;
  status: number;
}

const ProgramFee2 = ({ data }: { data: { data?: ProgramFee2Data } }) => {
  const feeData = data?.data;
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);
  const previousTabIdRef = useRef<string | null>(null);

  const sortedCategories = useMemo(() => {
    if (!feeData?.categories) return [];
    return [...feeData.categories].sort(
      (a, b) => a.order_index - b.order_index,
    );
  }, [feeData?.categories]);

  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const currentTabId = activeTabId ?? sortedCategories[0]?.id ?? null;

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_fee_section");
  //   }
  // }, []);

  // Set initial tab id for tab-switch tracking (so first user switch is tracked)
  useEffect(() => {
    if (sortedCategories.length > 0 && previousTabIdRef.current === null) {
      previousTabIdRef.current = sortedCategories[0].id;
    }
  }, [sortedCategories]);

  const handleTabChange = (catId: string, catTitle: string) => {
    if (previousTabIdRef.current !== catId && previousTabIdRef.current !== null) {
      tabSwitched({
        section: "program_fee_section",
        component: "fee_tabs",
        label: catTitle || `tab_${catId}`,
      });
    }
    previousTabIdRef.current = catId;
    setActiveTabId(catId);
  };

  const activeCategory = useMemo(
    () => sortedCategories.find((c) => c.id === currentTabId),
    [sortedCategories, currentTabId],
  );

  const normalRows = useMemo(() => {
    if (!activeCategory?.metadata) return [];
    return activeCategory.metadata
      .filter((r) => r.status && !r.is_total)
      .sort((a, b) => a.priority - b.priority);
  }, [activeCategory]);

  const totalRow = useMemo(() => {
    if (!activeCategory?.metadata) return null;
    return activeCategory.metadata.find((r) => r.status && r.is_total) ?? null;
  }, [activeCategory]);

  const benefits = useMemo(() => {
    if (!feeData?.metadata) return [];
    return [...feeData.metadata]
      .filter((m) => m.status)
      .sort((a, b) => a.priority - b.priority);
  }, [feeData?.metadata]);

  const features = useMemo(() => {
    if (!feeData?.metadata_1) return [];
    return [...feeData.metadata_1]
      .filter((m) => m.status)
      .sort((a, b) => a.priority - b.priority);
  }, [feeData?.metadata_1]);

  if (!feeData?.status || sortedCategories.length === 0) {
    return null;
  }

  const {
    title_1,
    title_1_color,
    title_2,
    title_2_gradient_from,
    title_2_gradient_to,
    title_2_gradient_angle,
    table_gradient_from,
    table_gradient_to,
    metadata_title,
    metadata_title_color,
    cta_text,
    cta_text_color,
    cta_gradient_from,
    cta_gradient_to,
    cta_gradient_angle,
    background_color,
    enable_animation,
  } = feeData;

  const isAnimationEnabled =
    enable_animation === true || enable_animation === 1;

  const title2GradientAngle = title_2_gradient_angle ?? 90;
  const ctaGradientAngle = cta_gradient_angle ?? -74.2;

  return (
    <section
      ref={ref}
      className="flex items-center justify-center w-full px-[24px] py-[40px] sm:px-[60px] sm:py-[60px] lg:px-[80px] lg:py-[40px] xl:px-[140px] xl:py-[80px]"
      style={{ backgroundColor: background_color || "#ffffff" }}
    >
      <div className="flex flex-col w-full sm:max-w-[1080px] md:max-w-[1230px] lg:max-w-[1430px] xl:max-w-[1750px]">
        {/* Title */}
        {(title_1 || title_2) && (
          <div className="flex flex-col gap-[2px] mb-[24px] sm:mb-[40px]">
            {title_1 && (
              <p
                className="dark-title-wf"
                style={{
                  fontFamily: "var(--font-google-sans)",
                  color: title_1_color || undefined,
                }}
              >
                {title_1}
              </p>
            )}
            {title_2 && (
              <p
                className="gradient-title-denton-blue"
                style={{
                  backgroundImage: `linear-gradient(${title2GradientAngle}deg, ${title_2_gradient_from}, ${title_2_gradient_to})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {title_2}
              </p>
            )}
          </div>
        )}

        {/* Main Content: Fee Table + Benefits */}
        <div className="flex flex-col sm:flex-row gap-[40px] mb-[40px]">
          {/* Left: Tabs + Fee Breakdown */}
          <div className="flex flex-col gap-[24px] sm:gap-[20px] w-full sm:w-[530px] md:w-[612px] lg:w-[693px] xl:w-[863px] shrink-0">
            {/* Tabs */}
            {sortedCategories.length > 1 && (
              <div className="flex items-center gap-[12px] overflow-x-auto sm:overflow-x-visible scrollbar-hide">
                {sortedCategories.map((cat, indx) => {
                  const isActive = cat.id === currentTabId;
                  return (
                    <RevealOnScroll
                      key={cat.id}
                      y={0}
                      x={-100 * (indx + 1)}
                      delay={indx * 100}
                      enableAnimation={isAnimationEnabled}
                    >
                    <button
                      key={cat.id}
                      data-cta-name="tab_switch"
                      onClick={() => handleTabChange(cat.id, cat.title)}
                      className={cn(
                        "px-[16px] py-[10px] rounded-[40px] text-[16px] sm:text-[14px] lg:text-[16px] font-semibold leading-[1.49] transition-colors shrink-0",
                      )}
                      style={{
                        fontFamily: "var(--font-google-sans)",
                        backgroundColor: isActive
                          ? cat.active_bg_color || "#02a7b6"
                          : cat.bg_color || "transparent",
                        color: isActive
                          ? cat.active_text_color || "#ffffff"
                          : cat.text_color || "#000000",
                        border: `1px solid ${isActive ? cat.active_bg_color || "#02a7b6" : cat.border_color || "rgba(0,0,0,0.4)"}`,
                      }}
                    >
                      {cat.title}
                    </button>
                    </RevealOnScroll>
                  );
                })}
              </div>
            )}

            {/* Fee Breakdown Card */}
            <div
              className="flex flex-col gap-[4px] sm:gap-[2px] p-[10px] sm:p-[16px] md:p-[20px] w-full text-white"
              style={{
                background: `linear-gradient(to bottom, ${table_gradient_from || "#342b7c"}, ${table_gradient_to || "#02a7b6"})`,
              }}
            >
              {normalRows.map((row, indx) => (
                <RevealOnScroll
                  key={row.id}
                  y={-100}
                  delay={indx * 100}
                  enableAnimation={isAnimationEnabled}
                >
                <div
                  key={row.id}
                  className="flex items-center justify-between p-[8px] sm:px-[16px] sm:py-[12px] md:px-[20px] md:py-[14px] lg:px-[24px] lg:py-[16px] gap-[20px]"
                >
                  <p
                    className="text-[14px] md:text-[16px] lg:text-[18px] font-medium leading-normal flex-1"
                    style={{
                      fontFamily: "var(--font-google-sans)",
                      color: row.text_color || undefined,
                    }}
                  >
                    {row.label}
                  </p>
                  <p
                    className="text-[14px] md:text-[16px] lg:text-[18px] font-medium leading-normal text-center shrink-0"
                    style={{
                      fontFamily: "var(--font-google-sans)",
                      color: row.value_text_color || undefined,
                      fontSize: row.value_font_size
                        ? `${row.value_font_size}px`
                        : undefined,
                    }}
                  >
                    {row.value}
                  </p>
                </div>
                </RevealOnScroll>
              ))}

              {/* Total Row */}
              {totalRow && (
                <RevealOnScroll
                  key={totalRow.id}
                  y={-500}
                  delay={Math.min(normalRows.length * 300, 700)}
                  enableAnimation={isAnimationEnabled}
                >
                <div className="flex items-center justify-between p-[8px] sm:px-[16px] sm:py-[12px] md:px-[20px] md:py-[14px] lg:px-[24px] lg:py-[16px] gap-[20px] border-t border-white/60">
                  <p
                    className="text-[14px] md:text-[16px] lg:text-[18px] font-medium leading-normal shrink-0"
                    style={{
                      fontFamily: "var(--font-google-sans)",
                      color: totalRow.text_color || undefined,
                    }}
                  >
                    {totalRow.label}
                  </p>
                  <p
                    className="text-[40px] sm:text-[44px] md:text-[54px] lg:text-[64px] font-bold leading-[1.2] tracking-[1.2px] sm:tracking-[1.32px] md:tracking-[1.62px] lg:tracking-[1.92px] flex-1 text-right sm:text-left sm:flex-none sm:shrink-0"
                    style={{
                      fontFamily: "var(--font-merriweather)",
                      color: totalRow.value_text_color || undefined,
                      fontSize: totalRow.value_font_size
                        ? `${totalRow.value_font_size}px`
                        : undefined,
                    }}
                  >
                    {totalRow.value}
                  </p>
                </div>
                </RevealOnScroll>
              )}
            </div>
          </div>

          {/* Right: What You Get */}
          {benefits.length > 0 && (
            <div className="flex flex-col gap-[16px]">
              <RevealOnScroll
                y={0}
                x={0}
                duration={1200}
                enableAnimation={isAnimationEnabled}
              >
              {metadata_title && (
                <p
                  className="text-[24px] font-extrabold italic leading-[1.4] tracking-[0.24px]"
                  style={{
                    fontFamily: "var(--font-merriweather)",
                    color: metadata_title_color || "#000000",
                  }}
                >
                  {metadata_title}
                </p>
              )}
                </RevealOnScroll>
              <div
                className="flex flex-col gap-[8px] text-[14px] md:text-[16px] lg:text-[18px] font-semibold leading-[1.4] tracking-[0.14px] md:tracking-[0.16px] lg:tracking-[0.18px]"
                style={{ fontFamily: "var(--font-google-sans)" }}
              >
                {benefits.map((benefit, indx) => (
                  <RevealOnScroll
                    key={benefit.id}
                    y={50}
                    duration={800}
                    delay={indx * 100}
                    enableAnimation={isAnimationEnabled}
                  >
                  <div
                    key={benefit.id}
                    className="flex items-center gap-[10px] "
                  >
                    {benefit.icon && (
                      <div className="relative shrink-0 size-[20px] md:size-[24px]">
                        <Image
                          src={benefit.icon}
                          alt={benefit.icon_alt || benefit.label}
                          fill
                          className="object-contain"
                        />
                      </div>
                    )}
                    <p
                      className="line-clamp-2 "
                      style={{ color: benefit.text_color || "rgba(0,0,0,0.7)" }}
                    >
                      {benefit.label}
                    </p>
                  </div>
                  </RevealOnScroll>
                ))}
          
              </div>
            </div>
          )}
        </div>

        {/* Bottom Features */}
        {features.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-[24px] sm:gap-[40px] md:gap-[60px] lg:gap-[80px] w-full mb-[40px]">
            {features.map((feature, indx) => (
              <RevealOnScroll
                key={feature.id}
                y={0}
                x={-100 * (indx + 1)}
                delay={indx * 100}
                enableAnimation={isAnimationEnabled}
              >
              <div
                key={feature.id}
                className="flex items-center gap-[20px] sm:gap-[16px] lg:gap-[20px] flex-1 min-w-0 w-full sm:w-auto"
              >
                {feature.icon && (
                  <div className="relative shrink-0 size-[30px] sm:size-[28px] md:size-[32px] lg:size-[40px]">
                    <Image
                      src={feature.icon}
                      alt={feature.icon_alt || feature.label}
                      fill
                      className="object-contain"
                    />
                  </div>
                )}
                <div className="flex flex-col min-w-0">
                  <span
                    className="text-[16px] md:text-[18px] lg:text-[20px] font-semibold leading-[1.4] tracking-[0.16px] sm:tracking-[0.18px] lg:tracking-[0.2px]"
                    style={{
                      fontFamily: "var(--font-google-sans)",
                      color: feature.text_color || "#000000",
                    }}
                  >
                    {feature.label}
                  </span>
                  {feature.description && (
                    <span
                      className="text-[14px] md:text-[16px] lg:text-[18px] font-semibold leading-[1.4] tracking-[0.14px] sm:tracking-[0.18px]"
                      style={{
                        fontFamily: "var(--font-google-sans)",
                        color: feature.value_text_color || "rgba(0,0,0,0.7)",
                      }}
                    >
                      {feature.description}
                    </span>
                  )}
                </div>
              </div>
              </RevealOnScroll>
            ))}
          </div>
        )}

        {/* CTA Button */}
        {cta_text && (
          <RevealOnScroll y={50} enableAnimation={isAnimationEnabled}>
          <div className="flex justify-center w-full">
            <button
              onClick={() => {
                leadCtaClick({
                  section: "program_fee_section",
                  component: "cta_button",
                  label: cta_text,
                });
                dispatch(
                  handleOpenLeaddrop({
                    variant: LEAD_DROP_VARIANTS.REGISTER,
                  }),
                );
              }}
              className="w-full sm:w-auto px-[24px] py-[16px] text-[16px] font-semibold leading-[1.4] tracking-[0.16px] cursor-pointer"
              style={{
                fontFamily: "var(--font-google-sans)",
                color: cta_text_color || "#ffffff",
                backgroundImage: `linear-gradient(${ctaGradientAngle}deg, ${cta_gradient_from || "#02a7b6"} 32.49%, ${cta_gradient_to || "#342b7c"} 135%)`,
              }}
            >
              {cta_text}
            </button>
          </div>
          </RevealOnScroll>
        )}
      </div>
    </section>
  );
};

export default ProgramFee2;
